import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { publicSiteUrl } from '../core/public-url';
import { DsCardDirective } from '../ui';

interface TrustPage {
  eyebrow: string;
  title: string;
  description: string;
  updated: string;
  sections: readonly { title: string; paragraphs: readonly string[] }[];
}

const TRUST_PAGES: Record<string, TrustPage> = {
  methodology: {
    eyebrow: 'Assessment transparency',
    title: 'How the IQLabTest assessment works',
    description:
      'A plain-language explanation of question selection, scoring, score ranges, limitations, and responsible interpretation.',
    updated: '31 July 2026',
    sections: [
      {
        title: 'Purpose and intended use',
        paragraphs: [
          'IQLabTest is an educational, IQ-style reasoning assessment. It is designed to help adults explore patterns in their performance across abstract, numerical, verbal, and spatial tasks. The result is an indicative practice estimate, not a clinical finding, diagnosis, educational placement decision, or employment recommendation.',
          'A single online session cannot capture every form of intelligence. Motivation, familiarity, language, accessibility, sleep, stress, interruptions, screen size, and connection quality can all influence performance. Results should therefore be treated as a structured snapshot of one attempt.',
        ],
      },
      {
        title: 'Question selection',
        paragraphs: [
          'Each public assessment contains 40 questions selected on the server at the beginning of the session. The selection is balanced across four reasoning domains and randomized so consecutive attempts are unlikely to present the same sequence. Correct answers remain on the server while the assessment is active.',
          'Randomization supports test integrity, but it also means two sessions may not have identical difficulty. The reported range communicates some of that uncertainty. IQLabTest does not claim that alternate forms are psychometrically equivalent to professionally normed instruments.',
        ],
      },
      {
        title: 'The four reasoning domains',
        paragraphs: [
          'Abstract reasoning questions examine relationships, rules, and patterns that are not dependent on a specific school subject. Numerical reasoning questions examine sequences and quantitative relationships. Verbal reasoning questions examine analogies, categories, and relationships between words. Spatial reasoning questions examine mental rotation, orientation, and transformations.',
          'Domain summaries are descriptive. A lower domain result may identify useful practice material, but it is not evidence of a permanent weakness. Each domain contains a limited sample of tasks, so small differences should not be overinterpreted.',
        ],
      },
      {
        title: 'Scoring and the indicative estimate',
        paragraphs: [
          'The service first counts correct responses and produces domain totals. It then maps overall practice performance to an IQ-style estimate centred on the familiar 100-point convention. The displayed percentile is derived from a conventional distribution with a standard deviation of 15; it is contextual information rather than evidence from a representative IQLabTest norming sample.',
          'The score range is deliberately shown alongside the estimate. Measurement is never perfectly precise, and presenting a single number without uncertainty can create false confidence. Repeating the test soon after completion may also improve performance through familiarity rather than a durable change in cognitive ability.',
        ],
      },
      {
        title: 'What the result can and cannot tell you',
        paragraphs: [
          'The result can help you notice which task types felt intuitive, which required more time, and where deliberate practice may be useful. The answer review and domain profile are intended to support learning and reflection.',
          'The result cannot establish a medical condition, learning disability, giftedness classification, fitness for work, or eligibility for an organization. It should never be used as the only basis for decisions that materially affect a person. Anyone seeking a formal cognitive assessment should consult a qualified psychologist using validated instruments under controlled conditions.',
        ],
      },
      {
        title: 'Privacy, verification, and certificates',
        paragraphs: [
          'The candidate name, date of birth, completion date, result, and verification identifier are stored to create and verify the certificate. Anyone with the private verification link or QR code can view the verification record, so candidates should share it deliberately.',
          'Certificates confirm that the recorded session was completed and that the displayed result matches the IQLabTest record. They do not convert an indicative online result into a regulated qualification or clinical score. Data handling, retention, and individual rights are explained in the Privacy Policy and Data Retention Policy.',
        ],
      },
      {
        title: 'Quality and revision',
        paragraphs: [
          'We review question wording, localization, accessibility, completion data, and technical errors. Material changes to scoring, item selection, or interpretation should be documented and released with appropriate versioning. Security reports and credible content corrections are prioritized.',
          'This methodology page is part of our commitment to explain uncertainty rather than hide it. The product will continue to evolve, and this page will be updated when assessment mechanics or evidence claims change.',
        ],
      },
    ],
  },
  editorial: {
    eyebrow: 'Editorial standards',
    title: 'How IQLabTest creates and reviews information',
    description:
      'Our standards for authorship, evidence, corrections, responsible IQ claims, and transparent review.',
    updated: '31 July 2026',
    sections: [
      {
        title: 'Our publishing promise',
        paragraphs: [
          'IQLabTest publishes educational material about intelligence, cognitive testing, reasoning practice, and score interpretation. We aim to make technical ideas understandable without turning uncertain claims into facts. Articles should distinguish established concepts, reasonable interpretation, historical context, and speculation.',
          'Our content is informational. It does not replace medical, psychological, educational, legal, or employment advice. Pages about public figures explicitly explain when reported IQ scores are undocumented estimates.',
        ],
      },
      {
        title: 'Author and reviewer responsibilities',
        paragraphs: [
          'Every substantive guide should have a clear editorial owner responsible for scope, structure, accuracy, and revision. Reviewers check whether claims match the cited evidence, whether limitations are visible, and whether wording could encourage harmful overinterpretation.',
          'The current editorial owner is the IQLabTest Editorial Team. Specialist review status is stated only when a qualified named reviewer has actually completed that review. We do not imply professional endorsement through anonymous or invented credentials.',
        ],
      },
      {
        title: 'Evidence hierarchy',
        paragraphs: [
          'We prefer primary research, official manuals, professional bodies, public statistical agencies, and systematic reviews. High-quality explanatory sources may be used for context. Search popularity, marketing pages, unsourced lists, and repeated online claims are not treated as proof.',
          'Where experts disagree or evidence is limited, the article should say so. Correlation is not described as causation, group averages are not used to judge individuals, and historical estimates are identified as retrospective speculation.',
        ],
      },
      {
        title: 'Writing about scores and people',
        paragraphs: [
          'IQ scores are estimates influenced by instrument, norm group, testing conditions, age, and measurement error. We use ranges and percentiles carefully and avoid labels that reduce a person to one number. Strengths outside the measured domains—including creativity, judgment, knowledge, motivation, and social understanding—matter.',
          'Pages about famous people separate documented testing from biographical inference. If no reliable primary evidence exists, the page says that the score is unknown. Curiosity can be answered without presenting a viral number as verified history.',
        ],
      },
      {
        title: 'Corrections and updates',
        paragraphs: [
          'Readers can report a factual, accessibility, or localization problem. We assess the source, correct material errors, and update review dates when meaningful editorial work is completed. Minor typography changes do not justify presenting an old page as newly researched.',
          'Major corrections should preserve transparency about what changed. Commercial pressure, search ranking, or a desired score interpretation must not override evidence or responsible wording.',
        ],
      },
      {
        title: 'Independence and commercial content',
        paragraphs: [
          'Educational explanations are separated from product claims. Calls to try the assessment should not distort the answer to a reader’s question. If paid services, affiliate relationships, sponsorship, or advertising influence a page, that relationship should be disclosed clearly.',
          'Our legal policies govern purchases, refunds, digital content, privacy, and withdrawal rights. Editorial content does not create a warranty that a practice result is suitable for a consequential decision.',
        ],
      },
      {
        title: 'Accessibility and inclusive language',
        paragraphs: [
          'Content should use headings, short paragraphs, descriptive links, readable contrast, keyboard-accessible controls, and meaningful alternative text. Language should be respectful across cultures and abilities. Translations should preserve meaning rather than merely copy sentence structure.',
          'We avoid language that treats cognitive performance as a measure of human worth. A calm, accurate explanation is more useful than sensational claims about genius, deficiency, or guaranteed improvement.',
        ],
      },
    ],
  },
};

@Component({
  selector: 'app-trust-page',
  imports: [DsCardDirective],
  templateUrl: './trust-page.html',
  styleUrl: './trust-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrustPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly document = inject(DOCUMENT);
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  protected readonly page =
    TRUST_PAGES[this.route.snapshot.data['trustPage'] as string] ?? TRUST_PAGES['methodology'];

  constructor() {
    const slug = this.route.snapshot.data['trustPage'] as string;
    const canonical = publicSiteUrl(`/en/${slug}`);
    this.titleService.setTitle(`${this.page.title} | IQLabTest`);
    this.meta.updateTag({ name: 'description', content: this.page.description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow, max-image-preview:large' });
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = canonical;
  }
}
